import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { getClosestThumbIndex } from './helpers/getClosestThumbIndex'
import { translateThumbs } from './helpers/translateThumbs'
import { getTrackBackground } from './helpers/getTrackBackground'

const MAX_MARK_COUNT = 20

interface SliderProps {
  /** Array of all available options to choose between */
  values: readonly number[]
  /**
   * An array of the starting value(s) for the slider thumb(s)
   * - Values must be within [min, max]
   * - A single value renders a single-thumb slider
   * - Multiple values render a multi-thumb (range) slider
   */
  selectedValues: number[]
  /** Controls whether the value tooltip is shown above the thumb(s) during interaction. */
  shouldShowTooltipLabel?: boolean
  /** Disables all user interaction when set to true, thumbs non-interactive and keyboard input ignored */
  disabled?: boolean
  /** Callback fired continuously as the user moves a thumb. Provides the current array of slider values during interaction. */
  onChange: (values: number[]) => void
  /** Optional test identifier(s) used for automated testing. Injects data attributes onto internal slider elements. */
  testId?: string

  prefix?: string | null
  suffix?: string | null
}

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      values,
      selectedValues,
      shouldShowTooltipLabel = true,
      disabled = false,
      onChange,
      testId,
      prefix,
      suffix,
    },
    forwardedRef
  ) => {
    if (values.length < 2) {
      throw new Error('Slider requires at least two values')
    }

    if (selectedValues.length > 2) {
      throw new Error('Max 2 thumbs allowed for range slider')
    }

    if (!selectedValues.every(v => values.includes(v))) {
      throw new Error('selectedValues must be members of values[]')
    }

    const thumbRefs = React.useRef<React.RefObject<HTMLDivElement>[]>([])
    const trackRef = React.useRef<HTMLDivElement | null>(null)
    const activePointerId = React.useRef<number | null>(null)
    const hasMovedRef = React.useRef(false)
    const trackMetricsRef = React.useRef<{ left: number; width: number } | null>(null)

    const [draggedThumbIndex, setDraggedThumbIndex] = React.useState(-1)
    const [thumbZIndexes, setThumbZIndexes] = React.useState(values.map((_, i) => i))
    const [pressedThumbIndex, setPressedThumbIndex] = React.useState<number | null>(null)

    const isDragging = draggedThumbIndex !== -1
    const shouldRenderMarks = values.length <= MAX_MARK_COUNT
    const min = values[0]
    const max = values[values.length - 1]

    if (thumbRefs.current.length !== selectedValues.length) {
      thumbRefs.current = selectedValues.map(
        (_, i) => thumbRefs.current[i] ?? React.createRef<HTMLDivElement>()
      )
    }

    const selectedIndices = React.useMemo(
      () => selectedValues.map(v => values.indexOf(v)),
      [selectedValues, values]
    )

    const setTrackRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        trackRef.current = node
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      },
      [forwardedRef]
    )

    const getThumbs = React.useCallback(
      () =>
        thumbRefs.current.map(ref => ref.current).filter((el): el is HTMLDivElement => el !== null),
      []
    )

    const updateTrackMetrics = React.useCallback(() => {
      if (!trackRef.current) return
      const rect = trackRef.current.getBoundingClientRect()
      trackMetricsRef.current = { left: rect.left, width: rect.width }
    }, [])

    const raiseThumbZIndex = React.useCallback((activeIndex: number) => {
      setThumbZIndexes(prev => {
        const maxZ = Math.max(...prev)
        const activeZ = prev[activeIndex]

        return prev.map((z, i) => {
          if (i === activeIndex) return maxZ
          return z > activeZ ? z - 1 : z
        })
      })
    }, [])

    const getOffsets = React.useCallback(() => {
      const trackElement = trackRef.current
      if (!trackElement) return []

      const trackRect = trackElement.getBoundingClientRect()
      const trackPadding = getPaddingAndBorder(trackElement)

      return getThumbs().map((thumb, thumbIdx) => {
        const thumbRect = thumb.getBoundingClientRect()
        const thumbMargins = getMargin(thumb)

        const index = selectedIndices[thumbIdx]

        return {
          x:
            (thumbMargins.left + trackPadding.left) * -1 +
            (trackRect.width * index) / (values.length - 1) -
            thumbRect.width / 2,
          y: ((thumbRect.height - trackRect.height) / 2 + trackPadding.top) * -1,
        }
      })
    }, [getThumbs, selectedIndices, values.length])

    const onResize = React.useCallback(() => {
      updateTrackMetrics()
      translateThumbs(getThumbs(), getOffsets())
    }, [updateTrackMetrics, getOffsets, getThumbs])

    const onMove = React.useCallback(
      (clientX: number) => {
        const metrics = trackMetricsRef.current
        if (!metrics) return

        const percent = (clientX - metrics.left) / metrics.width
        const rawIndex = percent * (values.length - 1)

        let nextIndex = Math.round(rawIndex)
        nextIndex = Math.max(0, Math.min(values.length - 1, nextIndex))

        const prevIndex = selectedIndices[draggedThumbIndex - 1]
        const nextLimit = selectedIndices[draggedThumbIndex + 1]

        if (prevIndex !== undefined && nextIndex < prevIndex) return
        if (nextLimit !== undefined && nextIndex > nextLimit) return

        if (nextIndex !== selectedIndices[draggedThumbIndex]) {
          const next = replaceAt(selectedValues, draggedThumbIndex, values[nextIndex])
          onChange(next)
        }
      },
      [draggedThumbIndex, selectedIndices, selectedValues, values, onChange]
    )

    const onPointerDown = React.useCallback(
      (e: React.PointerEvent, index?: number) => {
        if (disabled || e.button !== 0) return

        if (index) setPressedThumbIndex(index)

        const clientX = e.clientX
        const target = e.target as Node
        hasMovedRef.current = false
        const thumbIndex = thumbRefs.current.findIndex(
          ref => ref.current && ref.current.contains(target)
        )

        if (thumbIndex !== -1) {
          setDraggedThumbIndex(thumbIndex)
          raiseThumbZIndex(thumbIndex)
          thumbRefs.current[thumbIndex]?.current?.focus()
        } else {
          const thumbs = thumbRefs.current
            .map(ref => ref.current)
            .filter((el): el is HTMLDivElement => el !== null)

          const index = getClosestThumbIndex(thumbs, clientX)
          setDraggedThumbIndex(index)
          raiseThumbZIndex(index)
          thumbRefs.current[index]?.current?.focus()
        }

        e.currentTarget.setPointerCapture(e.pointerId)
        activePointerId.current = e.pointerId
        e.preventDefault()
      },
      [disabled, raiseThumbZIndex]
    )

    const onPointerMove = React.useCallback(
      (e: React.PointerEvent) => {
        if (activePointerId.current !== e.pointerId) return
        hasMovedRef.current = true
        if (draggedThumbIndex === -1) return

        onMove(e.clientX)
      },
      [onMove, draggedThumbIndex]
    )

    const onPointerUp = React.useCallback(
      (e: React.PointerEvent) => {
        if (activePointerId.current !== e.pointerId) return

        setPressedThumbIndex(null)

        e.currentTarget.releasePointerCapture(e.pointerId)
        activePointerId.current = null

        if (!hasMovedRef.current) onMove(e.clientX)

        hasMovedRef.current = false
        setDraggedThumbIndex(-1)
      },
      [onMove]
    )

    const onKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        const targetIndex = getThumbs().findIndex(
          child => child === e.target || child.contains(e.target as Node)
        )
        if (targetIndex === -1) return

        const direction = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
        if (!direction) return

        e.preventDefault()

        const current = selectedIndices[targetIndex]
        const nextIndex = Math.max(0, Math.min(values.length - 1, current + direction))

        if (nextIndex !== current) {
          const next = replaceAt(selectedValues, targetIndex, values[nextIndex])
          onChange(next)
        }
      },
      [onChange, getThumbs, selectedIndices, selectedValues, values]
    )

    const onKeyUp = React.useCallback(() => {
      setDraggedThumbIndex(-1)
    }, [])

    React.useEffect(() => {
      const trackEl = trackRef.current
      if (!trackEl) return

      const ResizeObs: typeof ResizeObserver | undefined =
        typeof window !== 'undefined' && 'ResizeObserver' in window ? ResizeObserver : undefined
      const observer = ResizeObs
        ? new ResizeObs(onResize)
        : {
            observe: () => window.addEventListener('resize', onResize),
            unobserve: () => window.removeEventListener('resize', onResize),
          }
      observer.observe(trackEl)
      checkInitialOverlap(selectedValues)

      return () => {
        observer.unobserve(trackEl)
      }
    }, [onResize, selectedValues])

    React.useLayoutEffect(() => {
      updateTrackMetrics()
    }, [updateTrackMetrics])

    React.useLayoutEffect(() => {
      translateThumbs(getThumbs(), getOffsets())
    }, [getOffsets, getThumbs])

    return (
      <SliderStyled
        className='slider'
        style={{
          padding: shouldShowTooltipLabel
            ? `5.7rem ${spacing.bigx1} ${spacing.md} ${spacing.bigx1}`
            : `${spacing.bigx3} 0 ${spacing.md} 0`,
        }}
      >
        <div
          ref={setTrackRef}
          className='slider__track'
          style={{
            position: 'relative',
            transform: 'scale(1)',
            width: '100%',
            borderRadius: radius.pill,
            alignSelf: 'center',
            cursor: isDragging
              ? 'grabbing'
              : values.length === 1 && !disabled
                ? 'pointer'
                : 'inherit',
            height: spacing.smx1,

            background: getTrackBackground({
              values: selectedValues,
              colors:
                selectedValues.length === 1
                  ? [colors.neutral[200]]
                  : [colors.neutral[200], colors.studio[600], colors.neutral[200]],
              min,
              max,
            }),
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {shouldRenderMarks &&
            values.map((value, index) => (
              <div
                key={`mark-${index}`}
                className='slider__mark'
                style={{
                  position: 'absolute',
                  left: `${(index / (values.length - 1)) * 100}%`,
                }}
              />
            ))}

          {selectedValues.map((value, index) => {
            const displayValue =
              selectedValues[index] < 0 ? Math.abs(selectedValues[index]) : selectedValues[index]

            let label: string | number = displayValue
            if (suffix === '%') label = `${displayValue}${suffix}`

            if (index === 1 && suffix && displayValue === max) {
              label = `${displayValue}${suffix}`
            }

            return (
              <div
                key={index}
                ref={thumbRefs.current[index]}
                className='slider__thumb'
                data-active={pressedThumbIndex === index}
                tabIndex={disabled ? undefined : 0}
                role='slider'
                aria-valuenow={value}
                aria-valuemin={values[index - 1] ?? min}
                aria-valuemax={values[index + 1] ?? max}
                aria-label={String(label)}
                onPointerDown={e => onPointerDown(e, index)}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                draggable={false}
                style={{
                  zIndex: thumbZIndexes[index],
                  cursor: disabled ? 'inherit' : isDragging ? 'grabbing' : 'grab',
                }}
                data-testid={testId}
              >
                {shouldShowTooltipLabel && (
                  <div className='slider__tooltip'>
                    {prefix}
                    {label}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </SliderStyled>
    )
  }
)

Slider.displayName = 'Slider'

// =============
// Local Helpers
// =============

function checkInitialOverlap(values: number[]) {
  if (values.length < 2) return
  if (!values.slice(1).every((item, i) => values[i] <= item)) {
    throw new RangeError(`values={[${values}]} needs to be sorted`)
  }
}

function getMargin(element: Element) {
  const style = window.getComputedStyle(element)
  return {
    top: parseInt(style.marginTop, 10),
    bottom: parseInt(style.marginBottom, 10),
    left: parseInt(style.marginLeft, 10),
    right: parseInt(style.marginRight, 10),
  }
}

function getPaddingAndBorder(element: Element) {
  const style = window.getComputedStyle(element)
  return {
    top: parseInt(style.paddingTop, 10) + parseInt(style.borderTopWidth, 10),
    bottom: parseInt(style.paddingBottom, 10) + parseInt(style.borderBottomWidth, 10),
    left: parseInt(style.paddingLeft, 10) + parseInt(style.borderLeftWidth, 10),
    right: parseInt(style.paddingRight, 10) + parseInt(style.borderRightWidth, 10),
  }
}

function replaceAt(values: number[], index: number, value: number) {
  const ret = values.slice(0)
  ret[index] = value
  return ret
}

// ======
// Styles
// ======

const SliderStyled = styled.div({
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  width: '100%',
  background: 'none',

  '.slider__track': {
    position: 'relative',

    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: `-${spacing.sm}`,
      width: spacing.md,
      background: colors.neutral[200],
      borderTopLeftRadius: radius.pill,
      borderBottomLeftRadius: radius.pill,
    },

    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: `-${spacing.sm}`,
      width: spacing.md,
      background: colors.neutral[200],
      borderTopRightRadius: radius.pill,
      borderBottomRightRadius: radius.pill,
      zIndex: -1,
    },
  },

  '.slider__thumb': {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: spacing.bigx4,
    height: spacing.bigx1,
    borderRadius: radius.pill,
    border: `0.2rem solid ${colors.studio[600]}`,
    boxShadow: shadow.sm,
    background: colors.studio[500],
    outline: 'none',
    touchAction: 'none',
    userSelect: 'none',
    transition: '0.15s ease',
    WebkitTapHighlightColor: 'transparent',

    '&[data-active="true"]': {
      transition: 'transform 0.3s ease',
      transform: 'scale(0.95)',
    },
  },

  '.slider__tooltip': {
    position: 'absolute',
    top: `-${spacing.lgx1}`,
    boxSizing: 'border-box',
    color: colors.white,
    textAlign: 'center',
    fontFamily: font.family.primary,
    fontWeight: font.weight.semibold,
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    borderRadius: radius.md,
    backgroundColor: colors.studio[500],
    padding: `${spacing.smx1} ${spacing.sm}`,
    boxShadow: shadow.md,

    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      top: 'calc(100% - 0.1rem)',
      width: '0',
      height: '0',
      borderLeft: `${spacing.smx1} solid transparent`,
      borderRight: `${spacing.smx1} solid transparent`,
      borderTop: `${spacing.smx1} solid ${colors.studio[500]}`,
      background: 'transparent',
      clear: 'both',
    },
  },

  '.slider__mark': {
    top: '-2.6rem',
    width: spacing.smx2,
    height: spacing.smx2,
    borderRadius: radius.full,
    background: colors.neutral[300],
  },
})
